import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchExpiringContracts,
  fetchExpiringAdminRentals
} from '../../../Redux/Actions/actions';
import {
  FaCalendarAlt,
  FaExclamationTriangle,
  FaHome,
  FaUser
} from 'react-icons/fa';
import {
  RENTAL_TYPE,
  formatCurrency,
  formatDate,
  getDetailPath
} from '../utils/rentalTypeHelpers';
import { devLog } from '../../../utils/devLog';
import style from './ExpiringContracts.module.css';

const ExpiringContracts = () => {
  const dispatch = useDispatch();
  const {
    expiringContracts,
    expiringAdminRentals,
    rentalsLoading,
    adminRentalsLoading,
    rentalsError,
    adminRentalsError
  } = useSelector((state) => state);
  const [daysFilter, setDaysFilter] = useState(90);
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    dispatch(fetchExpiringContracts(daysFilter));
    dispatch(fetchExpiringAdminRentals(daysFilter));
  }, [dispatch, daysFilter]);

  useEffect(() => {
    devLog('ExpiringContracts/data', {
      regularCount: expiringContracts?.length ?? 0,
      adminCount: expiringAdminRentals?.length ?? 0,
      rentalsError,
      adminRentalsError
    });
  }, [expiringContracts, expiringAdminRentals, rentalsError, adminRentalsError]);

  const getDaysToExpiry = (endDate) => {
    const today = new Date();
    const expiry = new Date(endDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getUrgencyLevel = (days) => {
    if (days <= 30) return 'critical';
    if (days <= 60) return 'warning';
    return 'info';
  };

  const getUrgencyText = (days) => {
    if (days <= 0) return 'Vencido';
    if (days <= 30) return 'Crítico';
    if (days <= 60) return 'Urgente';
    return 'Próximo a vencer';
  };

  const combined = useMemo(() => {
    const reg = (expiringContracts || []).map((c) => ({
      _type: RENTAL_TYPE.REGULAR,
      raw: c,
      id: c.id,
      contractEnd: c.contractEnd,
      contractStart: c.contractStart,
      title: c.property?.title || `Contrato #${c.id}`,
      address: `${c.property?.street || ''} ${
        c.property?.number || ''
      }, ${c.property?.locality || ''}`.trim(),
      tenantName:
        c.tenants?.find((t) => t.tenantType === 'main')?.fullName ||
        'No asignado',
      currentRentValue: c.currentRentValue
    }));
    const adm = (expiringAdminRentals || []).map((c) => ({
      _type: RENTAL_TYPE.ADMIN,
      raw: c,
      id: c.id,
      contractEnd: c.contractEnd || c.endDate,
      contractStart: c.contractStart || c.startDate,
      title:
        c.property?.title ||
        c.propertyTitle ||
        `Solo Admin #${c.id}`,
      address:
        c.propertyAddress ||
        `${c.property?.street || ''} ${
          c.property?.number || ''
        }, ${c.property?.locality || ''}`.trim(),
      tenantName:
        c.tenants?.find((t) => t.tenantType === 'main')?.fullName ||
        c.mainTenantName ||
        'No asignado',
      currentRentValue: c.currentRentValue || c.rentValue
    }));
    return [...reg, ...adm];
  }, [expiringContracts, expiringAdminRentals]);

  const filtered = useMemo(() => {
    if (typeFilter === 'all') return combined;
    return combined.filter((c) => c._type === typeFilter);
  }, [combined, typeFilter]);

  const isLoading = rentalsLoading || adminRentalsLoading;
  // Se muestra si CUALQUIERA de los dos tipos falla, indicando cuál (antes solo se mostraba si ambos fallaban).
  const errorMessage =
    rentalsError && adminRentalsError
      ? `Regular: ${rentalsError} | Admin: ${adminRentalsError}`
      : rentalsError
      ? `Regular: ${rentalsError}`
      : adminRentalsError
      ? `Admin: ${adminRentalsError}`
      : null;

  if (isLoading && combined.length === 0) {
    return (
      <div className={style.loading}>Cargando contratos por vencer...</div>
    );
  }
  if (errorMessage && combined.length === 0) {
    return <div className={style.error}>Error: {errorMessage}</div>;
  }

  const sorted = [...filtered].sort(
    (a, b) => getDaysToExpiry(a.contractEnd) - getDaysToExpiry(b.contractEnd)
  );

  const criticalCount = filtered.filter(
    (c) => getDaysToExpiry(c.contractEnd) <= 30
  ).length;
  const warningCount = filtered.filter(
    (c) => getDaysToExpiry(c.contractEnd) <= 60
  ).length;

  return (
    <div className={style.container}>
      {errorMessage && combined.length > 0 && (
        <div className={style.error}>Atención: {errorMessage}</div>
      )}
      <div className={style.header}>
        <h1 className={style.title}>
          <FaCalendarAlt className={style.titleIcon} />
          Contratos por Vencer
        </h1>

        <div className={style.filters}>
          <label className={style.filterLabel}>
            Mostrar contratos que vencen en:
          </label>
          <select
            value={daysFilter}
            onChange={(e) => setDaysFilter(Number(e.target.value))}
            className={style.filterSelect}
          >
            <option value={30}>30 días</option>
            <option value={60}>60 días</option>
            <option value={90}>90 días</option>
            <option value={180}>6 meses</option>
          </select>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          padding: '12px 0',
          flexWrap: 'wrap'
        }}
      >
        {[
          { v: 'all', l: 'Todos' },
          { v: RENTAL_TYPE.REGULAR, l: 'Regular' },
          { v: RENTAL_TYPE.ADMIN, l: 'Solo Admin' }
        ].map((opt) => (
          <button
            key={opt.v}
            type="button"
            onClick={() => setTypeFilter(opt.v)}
            style={{
              padding: '6px 12px',
              borderRadius: 999,
              border: 'none',
              cursor: 'pointer',
              background: typeFilter === opt.v ? '#1e40af' : '#e5e7eb',
              color: typeFilter === opt.v ? '#fff' : '#374151',
              fontWeight: 600,
              fontSize: '0.85rem'
            }}
          >
            {opt.l}
          </button>
        ))}
      </div>

      <div className={style.summary}>
        <div className={style.summaryCard}>
          <span className={style.summaryLabel}>Total Contratos</span>
          <span className={style.summaryValue}>{filtered.length}</span>
        </div>
        <div className={style.summaryCard}>
          <span className={style.summaryLabel}>Críticos (≤30 días)</span>
          <span className={`${style.summaryValue} ${style.critical}`}>
            {criticalCount}
          </span>
        </div>
        <div className={style.summaryCard}>
          <span className={style.summaryLabel}>Urgentes (≤60 días)</span>
          <span className={`${style.summaryValue} ${style.warning}`}>
            {warningCount}
          </span>
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className={style.noData}>
          <FaCalendarAlt className={style.noDataIcon} />
          <h3>No hay contratos próximos a vencer</h3>
          <p>En los próximos {daysFilter} días</p>
        </div>
      ) : (
        <div className={style.contractsList}>
          {sorted.map((contract) => {
            const daysToExpiry = getDaysToExpiry(contract.contractEnd);
            const urgencyLevel = getUrgencyLevel(daysToExpiry);
            const isAdmin = contract._type === RENTAL_TYPE.ADMIN;

            return (
              <div
                key={`${contract._type}-${contract.id}`}
                className={`${style.contractCard} ${style[urgencyLevel]}`}
              >
                <div className={style.cardHeader}>
                  <div className={style.propertyInfo}>
                    <FaHome className={style.propertyIcon} />
                    <div>
                      <h3 className={style.propertyTitle}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 999,
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            background: isAdmin ? '#fef3c7' : '#dbeafe',
                            color: isAdmin ? '#92400e' : '#1e40af',
                            marginRight: 8,
                            verticalAlign: 'middle'
                          }}
                        >
                          {isAdmin ? 'Admin' : 'Regular'}
                        </span>
                        {contract.title}
                      </h3>
                      <p className={style.propertyAddress}>
                        {contract.address}
                      </p>
                    </div>
                  </div>

                  <div
                    className={`${style.urgencyBadge} ${style[urgencyLevel]}`}
                  >
                    <FaExclamationTriangle />
                    {getUrgencyText(daysToExpiry)}
                  </div>
                </div>

                <div className={style.cardContent}>
                  <div className={style.contractInfo}>
                    <div className={style.infoItem}>
                      <FaUser className={style.infoIcon} />
                      <div>
                        <span className={style.infoLabel}>
                          Inquilino Principal
                        </span>
                        <span className={style.infoValue}>
                          {contract.tenantName}
                        </span>
                      </div>
                    </div>

                    <div className={style.infoItem}>
                      <FaCalendarAlt className={style.infoIcon} />
                      <div>
                        <span className={style.infoLabel}>
                          Fecha de Vencimiento
                        </span>
                        <span className={style.infoValue}>
                          {formatDate(contract.contractEnd)}
                        </span>
                        <span
                          className={`${style.daysLeft} ${style[urgencyLevel]}`}
                        >
                          {daysToExpiry > 0
                            ? `${daysToExpiry} días restantes`
                            : 'Vencido'}
                        </span>
                      </div>
                    </div>

                    <div className={style.infoItem}>
                      <span className={style.infoLabel}>
                        Duración del Contrato
                      </span>
                      <span className={style.infoValue}>
                        {formatDate(contract.contractStart)} -{' '}
                        {formatDate(contract.contractEnd)}
                      </span>
                    </div>

                    <div className={style.infoItem}>
                      <span className={style.infoLabel}>
                        Alquiler Actual
                      </span>
                      <span className={style.infoValue}>
                        {formatCurrency(contract.currentRentValue)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={style.cardActions}>
                  <Link
                    to={getDetailPath(contract.id, contract._type)}
                    className={`${style.actionButton} ${style.primary}`}
                  >
                    Ver Contrato
                  </Link>
                  <Link
                    to={getDetailPath(
                      contract.id,
                      contract._type,
                      'inquilinos'
                    )}
                    className={`${style.actionButton} ${style.secondary}`}
                  >
                    Inquilinos
                  </Link>
                  <Link
                    to={getDetailPath(
                      contract.id,
                      contract._type,
                      'pagos'
                    )}
                    className={`${style.actionButton} ${style.secondary}`}
                  >
                    Pagos
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ExpiringContracts;
